import { makeAutoObservable, runInAction } from 'mobx';

import {
  AreaUnit,
  Currency,
  GetCombinedUserDataQuery,
  GetOrganizationsUsersQuery,
  OrderStatus,
  PricingPlan,
  StripeCustomerAddress,
} from '../../graphql/graphql';
import {
  getCombinedUserData,
  getOrganizationsUsers,
  getUserTotalArea,
  saveStripeCustomer,
  startFreeTrial as startFreeTrialAPI,
} from './api';
import { logError, tryGetFirstError } from '../../helpers/functions/api';
import { BaseRootStore } from '../rootStore/baseRootStore';
import Sentry from '../../configs/sentry';

export default class UserStore<IRootStore extends BaseRootStore> {
  rootStore: IRootStore;

  combinedData: {
    stripeCustomer: GetCombinedUserDataQuery['getStripeCustomer'] | null;
    userData: GetCombinedUserDataQuery['getUserData'] | null;
    loading: boolean;
    error: Error | null;
  } = {
    stripeCustomer: null,
    userData: null,
    loading: false,
    error: null,
  };

  userDataOrganizations: {
    orgs: NonNullable<
      GetOrganizationsUsersQuery['getUserData']
    >['organizations'];
    error: Error | null;
    loading: boolean;
  } = {
    orgs: null,
    error: null,
    loading: false,
  };

  userDataTotalArea: {
    area: number | null;
    error: Error | null;
    loading: boolean;
  } = {
    area: null,
    error: null,
    loading: false,
  };

  saveCustomer: {
    error: Error | null;
    loading: boolean;
  } = {
    error: null,
    loading: false,
  };

  selectedCountry = ''; // alpha2 code

  taxId = '';

  freeTrial: {
    inProgress: boolean;
    error: Error | null;
  } = {
    inProgress: false,
    error: null,
  };

  constructor(rootStore: IRootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  get selectedCountryInfo() {
    return this.rootStore.countriesStore.alpha2ToCountryMap[
      this.selectedCountry
    ];
  }

  get userDataCountryInfo() {
    return this.rootStore.countriesStore.alpha3ToCountryMap[
      this.combinedData.userData?.country ?? ''
    ];
  }

  get combinedDataLoaded() {
    return !!this.combinedData.stripeCustomer && !!this.combinedData.userData;
  }

  get userDataOrganizationsLoaded() {
    return !!this.userDataOrganizations.orgs;
  }

  get userDataTotalAreaLoaded() {
    return this.userDataTotalArea.area != null;
  }

  get hasFreeTrialOrder() {
    return !!this.combinedData.userData?.orders?.some(
      (order) => order?.plan === PricingPlan.FreeTrial,
    );
  }

  get hasPaidOrder() {
    return !!this.combinedData.userData?.orders?.some(
      (order) =>
        (order?.plan === PricingPlan.MonthlyPayAsYouGo ||
          order?.plan === PricingPlan.AnnualSubscription ||
          order?.plan === PricingPlan.Enterprise) &&
        order?.status === OrderStatus.InvoicePaid,
    );
  }

  reset() {
    runInAction(() => {
      this.combinedData = {
        stripeCustomer: null,
        userData: null,
        loading: false,
        error: null,
      };

      this.userDataOrganizations = {
        orgs: null,
        error: null,
        loading: false,
      };

      this.userDataTotalArea = {
        area: null,
        error: null,
        loading: false,
      };

      this.saveCustomer = {
        error: null,
        loading: false,
      };

      this.selectedCountry = '';
      this.taxId = '';

      this.freeTrial = {
        inProgress: false,
        error: null,
      };
    });
  }

  setSelectedCountry(country: string) {
    this.selectedCountry = country;
  }

  setTaxId(taxId: string) {
    this.taxId = taxId;
  }

  setStripeCustomerFromQueryParams(data: {
    id: string | null;
    currency: Currency | null;
  }) {
    if (!data.id) {
      return;
    }

    this.combinedData.stripeCustomer = {
      id: data.id ? data.id : this.combinedData.stripeCustomer?.id,
      currency: data.currency ?? this.combinedData.stripeCustomer?.currency,
    };
  }

  async saveStripeCustomer(input: {
    name: string;
    address: StripeCustomerAddress;
    taxIdData?: { id: string; type: string };
  }) {
    this.saveCustomer.loading = true;
    this.saveCustomer.error = null;

    let typedError: Error | null = null;

    try {
      const { data } = await saveStripeCustomer({
        name: input.name,
        address: input.address,
        ...(input.taxIdData ? { taxIdData: [input.taxIdData] } : {}),
      });

      if (!data?.saveStripeCustomer) {
        throw new Error('[UserStore][saveStripeCustomer] Data is empty.');
      }
    } catch (error) {
      typedError = tryGetFirstError(error);
      runInAction(() => {
        this.saveCustomer.error = typedError;
      });
      logError(typedError);
    } finally {
      runInAction(() => {
        this.saveCustomer.loading = false;
      });
    }

    return { error: typedError };
  }

  async fetchOrganizationsUsers(input: {
    areaUnit: AreaUnit | null | undefined;
  }) {
    if (
      this.userDataOrganizationsLoaded ||
      this.userDataOrganizations.loading
    ) {
      return;
    }

    this.userDataOrganizations.loading = true;
    this.userDataOrganizations.error = null;

    try {
      if (!input.areaUnit) {
        throw new Error(
          '[UserStore][fetchOrganizationsUsers] areaUnit is not provided.',
        );
      }

      const { data } = await getOrganizationsUsers({
        unit: input.areaUnit,
      });

      if (!data?.getUserData) {
        throw new Error(
          '[UserStore][fetchOrganizationsUsers] getUserData is empty.',
        );
      }

      runInAction(() => {
        this.userDataOrganizations.orgs = data.getUserData?.organizations ?? [];
      });
    } catch (error) {
      const typedError = tryGetFirstError(error);
      runInAction(() => {
        this.userDataOrganizations.error = typedError;
      });
      logError(typedError);
    } finally {
      runInAction(() => {
        this.userDataOrganizations.loading = false;
      });
    }
  }

  async fetchUserTotalArea(input: { areaUnit: AreaUnit }) {
    if (this.userDataTotalAreaLoaded || this.userDataTotalArea.loading) {
      return;
    }

    this.userDataTotalArea.loading = true;
    this.userDataTotalArea.error = null;

    try {
      const { data } = await getUserTotalArea({
        unit: input.areaUnit,
      });

      if (!data?.getUserData) {
        throw new Error(
          '[UserStore][fetchUserTotalArea] getUserData is empty.',
        );
      }

      runInAction(() => {
        this.userDataTotalArea.area = data.getUserData?.totalArea ?? 0;
      });
    } catch (error) {
      const typedError = tryGetFirstError(error);
      runInAction(() => {
        this.userDataTotalArea.error = typedError;
      });
      logError(typedError);
    } finally {
      runInAction(() => {
        this.userDataTotalArea.loading = false;
      });
    }
  }

  async fetchCombinedData() {
    if (this.combinedDataLoaded || this.combinedData.loading) {
      return;
    }

    this.combinedData.loading = true;
    this.combinedData.error = null;

    try {
      const { data } = await getCombinedUserData();

      if (!data?.getUserData) {
        throw new Error('[UserStore][fetchCombinedData] getUserData is empty.');
      }

      runInAction(() => {
        this.combinedData.userData = data.getUserData;
        Sentry.initUser({
          id: data?.getUserData?.uuid,
          email: data?.getUserData?.email,
        });

        if (data.getStripeCustomer) {
          this.combinedData.stripeCustomer = data.getStripeCustomer;
        }
      });
    } catch (error) {
      const typedError = tryGetFirstError(error);
      runInAction(() => {
        this.combinedData.error = typedError;
      });
      logError(typedError);
    } finally {
      runInAction(() => {
        this.combinedData.loading = false;
      });
    }
  }

  async startFreeTrial({ areaUnit }: { areaUnit: AreaUnit }) {
    this.freeTrial.inProgress = true;
    this.freeTrial.error = null;

    try {
      const { data } = await startFreeTrialAPI({ areaUnit });

      if (!data?.startFreeTrial) {
        throw new Error('[UserStore][startFreeTrial] Data is empty.');
      }

      return data.startFreeTrial.isActive;
    } catch (error) {
      const typedError = tryGetFirstError(error);
      runInAction(() => {
        this.freeTrial.error = typedError;
      });
      logError(typedError);

      return null;
    } finally {
      runInAction(() => {
        this.freeTrial.inProgress = false;
      });
    }
  }
}
